import { FileText, Newspaper, PlayCircle } from 'lucide-react';
import { useMemo, useState } from 'react';

import type { ArtigoDestaque } from '@/api/generated/model';
import { NotaEstrelas } from '@/features/dashboard/components/NotaEstrelas';
import { EstadoVazio } from '@/shared/components/estados';

type Ordem = 'recentes' | 'avaliados';

/**
 * Painel lateral do dashboard.
 *
 * "Últimos postados" e "mais bem avaliados" são duas ordenações da mesma lista,
 * então viram um toggle em vez de dois blocos — e a ordenação é no cliente,
 * porque a lista é curta e um round-trip por clique não se paga.
 *
 * Quando a B8 trouxer os materiais de verdade, o backend passa a devolver o
 * top-N e só isto aqui muda: nada.
 */
export function ArtigosDestaqueLista({ artigos }: { artigos: ArtigoDestaque[] }) {
  const [ordem, setOrdem] = useState<Ordem>('recentes');

  const ordenados = useMemo(() => {
    const copia = [...artigos];
    if (ordem === 'recentes') {
      return copia.sort((a, b) => Date.parse(b.publicadoEm) - Date.parse(a.publicadoEm));
    }
    // Sem nota vai para o fim: um artigo não avaliado não pode aparecer acima
    // de um com 4,9 só porque `null` ordena antes de número.
    return copia.sort((a, b) => (b.notaMedia ?? -1) - (a.notaMedia ?? -1));
  }, [artigos, ordem]);

  return (
    <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden flex flex-col">
      <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">Artigos</h3>

        <div className="grid grid-cols-2 gap-1 p-0.5 bg-brand/10 rounded-md shrink-0">
          {(
            [
              ['recentes', 'Recentes'],
              ['avaliados', 'Melhores'],
            ] as const
          ).map(([valor, rotulo]) => (
            <button
              key={valor}
              type="button"
              onClick={() => setOrdem(valor)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${
                ordem === valor ? 'bg-brand text-white shadow-sm' : 'text-brand hover:bg-brand/10'
              }`}
            >
              {rotulo}
            </button>
          ))}
        </div>
      </div>

      {ordenados.length === 0 ? (
        <EstadoVazio
          icone={Newspaper}
          titulo="Nenhum artigo publicado"
          descricao="Os materiais enviados pelos parceiros aparecem aqui depois de aprovados."
        />
      ) : (
        <ul className="divide-y divide-brand/5">
          {ordenados.map((artigo) => (
            <li key={artigo.artigoId} className="px-6 py-3 hover:bg-muted/40 transition-colors">
              <div className="flex items-start gap-3">
                <div className="p-1.5 rounded-md bg-brand/10 shrink-0">
                  {artigo.tipo === 'VIDEO' ? (
                    <PlayCircle className="w-4 h-4 text-brand" />
                  ) : (
                    <FileText className="w-4 h-4 text-brand" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground truncate">{artigo.titulo}</p>
                  <p className="text-xs text-muted-foreground truncate">{artigo.parceiroNome}</p>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <NotaEstrelas nota={artigo.notaMedia} total={artigo.totalAvaliacoes} />
                    <span className="text-xs text-muted-foreground">
                      {new Date(artigo.publicadoEm).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
